import { buildFetchConfig } from '@/utils/fetchUtils';

const createSSEProcessor = (
  buffer: string,
  onChunk: (line: string) => void,
): {
  process: (isFinal: boolean) => string;
} => {
  let localBuffer = buffer;

  const findBoundary = (): { index: number; length: number } | null => {
    const idxCRLF = localBuffer.indexOf('\r\n\r\n');
    const idxLF = localBuffer.indexOf('\n\n');
    if (idxCRLF !== -1 && (idxLF === -1 || idxCRLF < idxLF)) {
      return { index: idxCRLF, length: 4 };
    }
    if (idxLF !== -1) {
      return { index: idxLF, length: 2 };
    }
    return null;
  };

  const process = (isFinal: boolean): string => {
    let boundary = findBoundary();
    while (boundary) {
      const eventChunk = localBuffer.slice(0, boundary.index);
      localBuffer = localBuffer.slice(boundary.index + boundary.length);
      const lines = eventChunk.split(/\r?\n/);
      const dataPayload = lines
        .filter(line => line.startsWith('data:'))
        .map(line => line.replace(/^data:\s?/, ''))
        .join('\n')
        .trim();
      if (dataPayload && dataPayload !== '[DONE]') {
        onChunk(dataPayload);
      }
      boundary = findBoundary();
    }
    if (isFinal && localBuffer.trim()) {
      const lines = localBuffer.split(/\r?\n/);
      const dataPayload = lines
        .filter(line => line.startsWith('data:'))
        .map(line => line.replace(/^data:\s?/, ''))
        .join('\n')
        .trim();
      if (dataPayload && dataPayload !== '[DONE]') {
        onChunk(dataPayload);
      }
      localBuffer = '';
    }
    return localBuffer;
  };

  return { process };
};

const createJSONExtractor = (
  buffer: string,
  onChunk: (line: string) => void,
): {
  extract: () => string;
} => {
  let localBuffer = buffer;

  const extractJSON = (): string | null => {
    let depth = 0;
    let inString = false;
    let escape = false;
    let jsonEnd = -1;
    for (let i = 0; i < localBuffer.length; i++) {
      const char = localBuffer[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (char === '\\') {
        escape = true;
        continue;
      }
      if (char === '"') {
        inString = !inString;
        continue;
      }
      if (!inString) {
        if (char === '{') depth++;
        else if (char === '}') {
          depth--;
          if (depth === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
    }
    if (jsonEnd > 0) {
      const jsonStr = localBuffer.substring(0, jsonEnd);
      localBuffer = localBuffer.substring(jsonEnd);
      return jsonStr;
    }
    return null;
  };

  const extract = (): string => {
    let jsonStr = extractJSON();
    while (jsonStr) {
      onChunk(jsonStr);
      jsonStr = extractJSON();
    }
    return localBuffer;
  };

  return { extract };
};

export const streamingPost = async (
  url: string,
  data: unknown,
  onChunk: (line: string) => void,
  additionalHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  const { baseURL, headers } = buildFetchConfig(additionalHeaders);

  const response = await fetch(`${baseURL}${url}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    console.error('[lib_get_service] Request failed!');
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  if (!reader) throw new Error('Response body is not readable');

  const contentType = response.headers.get('content-type') || '';
  const isSSE = contentType.includes('text/event-stream');
  let buffer = '';
  let streaming = true;

  while (streaming) {
    const { done, value } = await reader.read();
    if (done) {
      if (isSSE) {
        const processor = createSSEProcessor(buffer, onChunk);
        buffer = processor.process(true);
      } else {
        const extractor = createJSONExtractor(buffer, onChunk);
        buffer = extractor.extract();
      }
      streaming = false;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    if (isSSE) {
      const processor = createSSEProcessor(buffer, onChunk);
      buffer = processor.process(false);
    } else {
      const extractor = createJSONExtractor(buffer, onChunk);
      buffer = extractor.extract();
    }
  }
};

export const streamingPostFormData = async (
  url: string,
  formData: FormData,
  onChunk: (line: string) => void,
  additionalHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> => {
  const { baseURL, headers } = buildFetchConfig(additionalHeaders);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { 'Content-Type': _, ...headersWithoutContentType } = headers;

  const response = await fetch(`${baseURL}${url}`, {
    method: 'POST',
    headers: headersWithoutContentType,
    body: formData,
    signal,
  });

  if (!response.ok) {
    console.error('[lib_get_service] FormData upload failed!');
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  if (!reader) throw new Error('Response body is not readable');

  const contentType = response.headers.get('content-type') || '';
  const isSSE = contentType.includes('text/event-stream');
  let buffer = '';
  let streaming = true;

  while (streaming) {
    const { done, value } = await reader.read();
    if (done) {
      if (isSSE) {
        const processor = createSSEProcessor(buffer, onChunk);
        buffer = processor.process(true);
      } else {
        const extractor = createJSONExtractor(buffer, onChunk);
        buffer = extractor.extract();
      }
      streaming = false;
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    if (isSSE) {
      const processor = createSSEProcessor(buffer, onChunk);
      buffer = processor.process(false);
    } else {
      const extractor = createJSONExtractor(buffer, onChunk);
      buffer = extractor.extract();
    }
  }
};
